import { useEffect } from 'react';

export interface PageMetaOptions {
  title: string;
  description: string;
  keywords: string;
  path: string;
  ogType?: string;
  ogImage?: string;
  robots?: string;
  jsonLd?: unknown;
}

const OG_FALLBACK_IMAGE =
  'https://readdy.ai/api/search-image?query=warm%20terracotta%20and%20gold%20editorial%20banner%20with%20abstract%20timeline%20markers%20on%20cream%20background%2C%20flat%20minimal%20graphic%20poster%2C%20no%20text&width=1200&height=630&seq=baoliao-og&orientation=landscape';

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function usePageMeta(options: PageMetaOptions) {
  const {
    title,
    description,
    keywords,
    path,
    ogType = 'website',
    ogImage = OG_FALLBACK_IMAGE,
    robots = 'index, follow',
    jsonLd,
  } = options;

  useEffect(() => {
    const base = (__BASE_PATH__ || '/').replace(/\/$/, '');
    const canonical = `${window.location.origin}${base}${path === '/' ? '/' : path}`;

    document.title = title;
    setMeta('name', 'description', description);
    setMeta('name', 'keywords', keywords);
    setMeta('name', 'robots', robots);

    setLink('canonical', canonical);

    setMeta('property', 'og:site_name', '爆料大赛');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:locale', 'zh_CN');

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImage);

    const scriptId = 'page-structured-data';
    document.getElementById(scriptId)?.remove();
    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = scriptId;
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      document.getElementById(scriptId)?.remove();
    };
  }, [title, description, keywords, path, ogType, ogImage, robots, jsonLd]);
}